/* eslint-disable @angular-eslint/no-output-on-prefix */

import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import * as Hammer from 'hammerjs';
import { gsap } from 'gsap';
import { POP_WIDTH_UNIT } from './pop-width-unit';
import { POP_DIRECTION } from './pop-direction';
import { POP_ALIGNMENT } from './pop-alignment';
import { POP_EVENT_TYPE } from './pop-event-type';
import { WindowRef } from '../../../vendor/window/window.component';
import { fromEvent, Subject, Observable } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import {
    Overlay,
    OverlayRef,
    GlobalPositionStrategy,
    ScrollStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { PopService } from './pop.service';
import { IPopDynamicEvent } from './pop-dynamic-event';
import { Platform } from '@angular/cdk/platform';
import { UIRouter } from '@uirouter/core';
import { PopParentElement } from '@components/pop/pop-parent-element';
import { Keyboard } from '@core/models/common/keyboard';
import { Debounce } from '@shared/decorators/debounce.decorator';
import { ScrollService } from '@shared/services/scroll.service';
import { getPopSizePosition, isPopoverWithinBoundaries, normalizePopDirection } from '@core/utils/pop';
import { getOffset } from '@core/utils/dom';
import { UIEventType } from '@core/models/ui/ui-event';
import { Capabilities } from '@core/utils/capabilities';
// use cdk overlay

@Component({
    selector: 'pay-pop',
    templateUrl: 'pop.component.html',
    styleUrls: [
        '../../../styles/includes.scss',
        './pop.component.scss'
        
    ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'c-pop'
    }
})
export class PopComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

    @Input()
    get show(): boolean {
        return this._show;
    }

    set show(v: boolean) {
        this._show = v;

        // This EventEmitter is necessary to emit the latest value by using the double binding technique '[(show)]'.
        // It requires the 'Change' suffix
        this.showChange.emit(this._show);
    }

    public arrowClass: string = '';
    public data: any = {};

    @Input() popoverId: string;
    @Input() popoverInstance: string;
    @Input() direction: POP_DIRECTION = POP_DIRECTION.BOTTOM;
    @Input() alignment: POP_ALIGNMENT = POP_ALIGNMENT.CENTER;
    @Input() fallbackDirection: POP_DIRECTION = POP_DIRECTION.OPPOSITE;
    @Input() modal: boolean = true;
    @Input() width?: number;
    @Input() extraWidth?: number;
    @Input() autoWidth: boolean = true;
    @Input() widthUnit: POP_WIDTH_UNIT = POP_WIDTH_UNIT.PX;
    @Input() overlay: boolean = false;
    @Input() arrow: boolean = false;
    @Input() autoHide: boolean = true;
    @Input() resizeWindowHide: boolean = true;
    @Input() delay: number = 0;
    @Input() gap: number = 10;
    @Input() dynamic: boolean = false;
    @Input() dynamicPosition?: any;
    @Input() bindingReady: boolean;
    @Input() transparency: boolean = false;
    @Input() closeOnClick: boolean = false;

    /**
     * The parent element in the form of x,y, width, height can be provided where the pop is not directly attached to a physical
     * DOM element
     */
    @Input()
    parentElement?: PopParentElement;

    /**
     * If you wish to force a backdrop for a dynamic pop. Otherwise dynamic pops don't have backdrops
     * This also means the popover won't close on mouseout, mouseenter events but rather only on backdrop clock
     */
    @Input()
    forceBackdrop?: boolean;

    /**
     * Since popover opens in cdk and any class on <pay-pop>
     * doesn't allow to target directly.
     * Use this class to give your popover a custom class that you can use to target the popover
     * to provide any custom styling if needed.
     */
    @Input()
    customPanelClass?: string = '';

    @Output() showChange: EventEmitter<boolean> = new EventEmitter();
    @Output() onBackdropClick: EventEmitter<{ type: POP_EVENT_TYPE }> = new EventEmitter();
    @Output() onClose: EventEmitter<{ type: POP_EVENT_TYPE }> = new EventEmitter();
    @Output() onOpen: EventEmitter<{ type: POP_EVENT_TYPE }> = new EventEmitter();

    @ViewChild('dynamicContainer', { read: ViewContainerRef, static: true }) public dynamicContainer: ViewContainerRef;
    @ViewChild('template', { static: true }) template: TemplateRef<any>;
    @ViewChild('customArrow', { read: ElementRef, static: false }) customArrow: ElementRef;

    @ContentChild(TemplateRef, { static: true }) dynamicTemplate;

    private nativeElement: HTMLElement;
    private nativeWindow: Window;
    private hammertime: any;
    private elements: {
        app: any,
        window: Window,
        body: HTMLElement,
        parentElement: HTMLElement,
        parentNode: any
    };
    private destroyed$: Subject<void> = new Subject();
    private popoverClosed$: Subject<void> = new Subject();
    private popStream$: Observable<any>;
    private scrollingNotifications$: Observable<any>;
    private popoverServiceRefresher$: Observable<any>;
    private needsRefreshing: boolean;
    private overlayRef: OverlayRef;
    private cssClasses = {
        root: 'c-pop',
        arrowRoot: 'c-pop__arrow--',
        backdrop: 'c-pop__backdrop',
        panel: 'c-pop__panel' ,
        panelWithTransparency: 'c-pop__panel--transparency'
    };
    private _show: boolean;
    private hover: boolean;
    private unBindRouterChange: any;
    private buffer: number = 16;
    private arrowBuffer: number = 15;

    constructor(private element: ElementRef,
                private uiRouter: UIRouter,
                private cdr: ChangeDetectorRef,
                private overlayService: Overlay,
                private popService: PopService,
                private scrollService: ScrollService,
                private platformService: Platform,
                private renderer: Renderer2,
                windowRef: WindowRef) {

        this.nativeElement = this.element.nativeElement;
        this.nativeWindow = windowRef.nativeWindow;
        this.hammertime = new Hammer(this.nativeElement);
    }

    ngOnInit() {
        this.checkDirectionAndAlignmentValidity();

        // NOTE:A: Check for alignment/direction errors here
        this.elements = this.getElements();

        if (!this.parentElement && this.elements.parentElement) {
            this.parentElement = this.coerceParentElement(this.elements.parentElement);
        }

        // Bind events to capture
        this.hammertime.on('tap', (e) => this.capturePopoverHandler);
        this.hammertime.on('panstart', (e) => this.capturePopoverHandler);
        this.hammertime.set({enable: false});

        // Remove the popover from the DOM.
        if (this.nativeElement.parentNode) {
            this.nativeElement.parentNode.removeChild(this.nativeElement);
        }

        fromEvent(this.nativeWindow, 'resize')
            .pipe(
                takeUntil(this.destroyed$)
            )
            .subscribe((e) => {
                if (this.resizeWindowHide) {
                    this.close();
                } else {
                    this.updatePositionAndArrow();
                }
            });

        if (this.dynamic) {
            if (!this.popService.isRegistered(this.popoverId, this.popoverInstance)) {
                this.popStream$ = this.popService.registerPopover(this.popoverId, this.popoverInstance);

                this.popStream$
                  .pipe(
                    takeUntil(this.destroyed$),
                    filter((event: IPopDynamicEvent) => event.id === this.popoverId && event.instance === this.popoverInstance)
                  )
                  .subscribe((event: IPopDynamicEvent) => {

                      const { element, show, context, force } = event;

                      if (!(!show && this.hover) || force) {
                          this.show = show;
                          this.data = context;
                      }

                      if (show) {
                          if (element) {
                              // if parent element is passed as HTMLElement
                              if (element instanceof HTMLElement || !element.top || !element.left) {
                                  this.parentElement = this.coerceParentElement(element as HTMLElement);
                              } else {
                                  this.parentElement = element;
                              }

                          }

                          this.data = context;

                          this.cdr.detectChanges();
                          this.open();

                      } else if (!show && (!this.hover || force)) {
                          this.close();
                      }

                      // this.needsRefreshing = true;
                      // this.render();
                      this.cdr.markForCheck();

                  });
            }

            this.popoverServiceRefresher$ = this.popService.getRefresher();

            this.popoverServiceRefresher$
                .pipe(
                    takeUntil(this.destroyed$),
                    filter((x) => x.id === this.popoverId && x.instance === this.popoverInstance)
                )
                .subscribe(() => {
                    this.needsRefreshing = true;
                });

            this.scrollingNotifications$ = this.scrollService.getScrollingNotifications();

            this.scrollingNotifications$
                .pipe(
                    takeUntil(this.destroyed$),
                    filter(() => this.dynamic && this.autoHide)
                )
                .subscribe((e) => {
                    if (this.show) {
                        this.popService.close(this.popoverId, this.popoverInstance);
                    }
                });
        }

        this.cdr.markForCheck();

    }

    ngOnDestroy() {
        if (this.overlayRef && this.show) {
            this.close();
        }

        if (this.dynamic) {
            this.popService.unregisterPopover(this.popoverId, this.popoverInstance);
        }

        this.destroyed$.next();
        this.destroyed$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes['show'] && !changes['show'].isFirstChange()
            && changes['show'].currentValue !== changes['show'].previousValue) {
            if (changes['show'].currentValue) {
                this.open();
            } else {
                this.close();
            }
        }

        if (changes && changes['parent'] && changes['position'].currentValue && this.show) {
            this.updatePositionAndArrow();
        }
    }

    ngAfterViewChecked() {
        if (this.needsRefreshing) {
            this.render();
        }
    }

    private open() {
        this.show = true;
        this.render();
        this.onOpen.emit({ type: POP_EVENT_TYPE.OPEN });
        this.cdr.markForCheck();
    }

    private close() {
        this.show = false;
        this.popoverClosed$.next();

        if (this.overlayRef) {
            this.overlayRef.dispose();
        }

        this.onClose.emit({ type: POP_EVENT_TYPE.CLOSE });
        this.cdr.markForCheck();
    }

    private getElements(): { app, window, body, parentElement, parentNode } {
        return {
            app: this.nativeWindow.document.getElementsByClassName('c-app')[0],
            window: this.nativeWindow,
            body: this.nativeWindow.document.body,
            parentElement: this.nativeElement.parentElement,
            parentNode: this.nativeElement.parentNode
        };
    }

    private capturePopoverHandler(event: Event): void {
        if (event.type === 'resize') {
            this.render();
        }

        // Check if clicking inside with autohide or outside popover
        if (this.autoHide) {
            if (event.type === 'resize'
              && (this.resizeWindowHide && !Capabilities.isMobile())) {
                this.debouncedHide();

            } else if (this.nativeElement[0]
              && this.nativeElement[0].hasOwnProperty('contains')
              && !this.nativeElement[0].contains(event.target)) {
                this.debouncedHide();
            }
        } else {
            if (event.type !== 'resize'
              || (event.type === 'resize' && this.resizeWindowHide)) {
                this.debouncedHide();
            }
        }
    }

    @Debounce(300)
    private debouncedHide() {
        if (this.show) {
            this.close();
        }
    }

    private render() {
        this.bindingReady = false;
        const templatePortal = this.dynamic
                                    ? new TemplatePortal(this.template, this.dynamicContainer, { data: this.data })
                                    : new TemplatePortal(this.template, this.dynamicContainer);
        this.overlayRef = this.buildOverlay();
        this.overlayRef.attach(templatePortal);
        this.cdr.detectChanges();
        this.updatePositionAndArrow();

        const overlayContainer: HTMLElement = this.overlayRef.overlayElement.parentElement.parentElement;
        this.bindOverlayEvents(this.overlayRef);
        this.bindingReady = true;
        this.cdr.markForCheck();
    }

    private bindOverlayEvents(overlayRef: OverlayRef) {
        overlayRef.backdropClick()
            .pipe(
                takeUntil(this.destroyed$),
                takeUntil(this.popoverClosed$)
            )
            .subscribe((event) => {

                if (this.onBackdropClick) {
                    this.onBackdropClick.emit();
                }

                this.close();
                this.cdr.detectChanges();
            });

        overlayRef.keydownEvents()
            .pipe(
                takeUntil(this.destroyed$),
                takeUntil(this.popoverClosed$)
            )
            .subscribe((event: KeyboardEvent) => {
                if (event && (event?.keyCode === Keyboard.ESC || event.code == Keyboard.ESC.toString())) {
                    this.close();
                    this.cdr.detectChanges();
                }
            });

        if (this.dynamic && !this.forceBackdrop) {
            fromEvent(overlayRef.overlayElement, UIEventType.MOUSEENTER)
                .pipe(
                    takeUntil(this.destroyed$),
                    takeUntil(this.popoverClosed$)
                )
                .subscribe((event) => {
                    this.hover = true;
                });

            fromEvent(overlayRef.overlayElement, UIEventType.MOUSELEAVE)
                .pipe(
                    takeUntil(this.destroyed$),
                    takeUntil(this.popoverClosed$)
                )
                .subscribe((event) => {
                    this.hover = false;
                    this.popService.close(this.popoverId, this.popoverInstance);
                });

            fromEvent(overlayRef.overlayElement, UIEventType.CLICK)
                .pipe(
                    filter(() => this.closeOnClick),
                    takeUntil(this.destroyed$),
                    takeUntil(this.popoverClosed$)
                )
                .subscribe((event) => {
                    this.hover = false;
                    this.popService.close(this.popoverId, this.popoverInstance);
                });
        }

        // create a route change subscription if not created to hide popover when there is route change
        if (!this.unBindRouterChange) {
            this.unBindRouterChange = this.uiRouter.urlService.onChange(() => {
                this.close();
                // de-register immediately once the route changes
                this.unBindRouterChange();
                this.unBindRouterChange = undefined;
            });
        }
    }

    private buildOverlay(): OverlayRef {

        let scrollStrategy: ScrollStrategy = this.overlayService.scrollStrategies.close();

        if (!this.dynamic && this.platformService.SAFARI) {
            scrollStrategy = this.overlayService.scrollStrategies.block();
        } else if (!this.autoHide) {
            scrollStrategy = this.overlayService.scrollStrategies.reposition();
        }

        // safari has a rubber band effect which emits a scroll event in overlay while scrolling inside the popover
        // so we use block scroll strategy to prevent that scroll in safari for popovers that are not dynamic
        const overlayRef: OverlayRef = this.overlayService.create({
            disposeOnNavigation: true,
            width: this.getPopoverWidth(),
            minHeight: '50px',
            hasBackdrop: (!this.dynamic || this.forceBackdrop) ? true : false,
            backdropClass: this.cssClasses.backdrop,
            scrollStrategy,
            panelClass: [this.cssClasses.panel, this.customPanelClass],
            positionStrategy: this.overlayService.position().global()
        });

        this.nativeElement.classList.forEach((cssClass) => {
            if (cssClass !== this.cssClasses.root) {
                this.renderer.addClass(overlayRef.overlayElement, cssClass);
            }
        });

        // TODO: J keep an eye don't think its not needed as the pops mostly close on resize
        // this.renderer.addClass(overlayRef.overlayElement, 'hidden-xs');
        // this.renderer.addClass(overlayRef.overlayElement, 'hidden-sm');

        // Attach transparency
        if (this.transparency) {
            overlayRef.addPanelClass(this.cssClasses.panelWithTransparency);
        }

        overlayRef.updatePosition();

        return overlayRef;
    }

    private getPopoverWidth() {
        // Define popover width
        let popoverWidth: number;
        const parentWidth: number = this.parentElement.nativeElement ? this.parentElement.nativeElement.clientWidth : (this.parentElement.width || 0);

        if (this.width) {
            popoverWidth = this.width;
        } else if (this.extraWidth > 0) {
            popoverWidth = this.extraWidth + parentWidth;
        } else if (this.autoWidth) {
            popoverWidth = parentWidth;
        }

        return popoverWidth;
    }

    private updatePositionAndArrow() {
        const popoverWidth: number = this.overlayRef.overlayElement.clientWidth;
        const popoverHeight: number = this.overlayRef.overlayElement.clientHeight;
        const windowWidth: number = this.nativeWindow.innerWidth;
        const windowHeight: number = this.nativeWindow.innerHeight;
        let arrowOffset: number = 0;
        let sizeAndPosition = getPopSizePosition(
          this.parentElement,
          popoverWidth,
          popoverHeight,
          this.alignment,
          this.direction,
          this.overlay,
          this.gap
        );

        const popoverLeft: number = Number(sizeAndPosition.left.replace('px', ''));
        const popoverTop: number = Number(sizeAndPosition.top.replace('px', ''));
        if (!isPopoverWithinBoundaries(popoverTop, popoverLeft, popoverWidth, popoverHeight, windowWidth, windowHeight) &&
            this.fallbackDirection !== this.direction) {

            if (popoverLeft < 1 || (popoverLeft + popoverWidth) > windowWidth) {
                // If popover is out of boundaries re-calculate using the fallback direction
                sizeAndPosition = getPopSizePosition(
                  this.parentElement,
                  popoverWidth,
                  popoverHeight,
                  this.alignment,
                  normalizePopDirection(this.direction, this.fallbackDirection),
                  this.overlay,
                  this.gap
                );
            }

            // Attempt to adjust heights to fit within the view
            if ((popoverTop + popoverHeight) > windowHeight) {
                const diff = windowHeight - (popoverTop + popoverHeight);
                sizeAndPosition.top = `${(popoverTop + diff) - this.buffer}px`;
                arrowOffset = this.arrowBuffer - diff;
            }
        }

        const globalPositionStrategy: GlobalPositionStrategy = this.overlayService.position().global().left(sizeAndPosition.left).top(sizeAndPosition.top);

        if (this.arrow) {
            this.arrowClass = this.cssClasses.arrowRoot + sizeAndPosition.arrowClass.replace('_', '-');
            gsap.set(this.customArrow.nativeElement, { top: arrowOffset });
            this.cdr.markForCheck();
        }

        this.overlayRef.updatePositionStrategy(globalPositionStrategy);
    }

    private checkDirectionAndAlignmentValidity() {
        const invalidCombinations = {
            [POP_DIRECTION.BOTTOM]: [POP_ALIGNMENT.TOP, POP_ALIGNMENT.BOTTOM],
            [POP_DIRECTION.TOP]: [POP_ALIGNMENT.TOP, POP_ALIGNMENT.BOTTOM],
            [POP_DIRECTION.LEFT]: [POP_ALIGNMENT.LEFT, POP_ALIGNMENT.RIGHT],
            [POP_DIRECTION.RIGHT]: [POP_ALIGNMENT.LEFT, POP_ALIGNMENT.RIGHT]
        };

        if (invalidCombinations[this.direction]?.includes(this.alignment)) {
            throw new Error(`Combination of direction: ${this.direction} and alignment: ${this.alignment} not allowed. Rendering results may be unexpected`);
        }
    }

    private coerceParentElement(element: HTMLElement): PopParentElement {
        const { borderRightWidth, borderBottomWidth } = this.nativeWindow.getComputedStyle(element);
        const { top, left } = getOffset(element);

        return {
            top,
            left,
            height: element.clientHeight,
            width: element.clientWidth,
            rightBorder: Number(borderRightWidth.replace('px', '')) || 0,
            bottomBorder: Number(borderBottomWidth.replace('px', '')) || 0,
            nativeElement: element
        };
    }
}
